import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, RotateCcw, Trophy, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { writeVisaRecommendation } from '@/hooks/useVisaRecommenderPreference';

/**
 * BestCountryRecommender
 * ───────────────────────────────────────────────────────────────────────
 * 3-question quiz that scores Germany / Australia / Canada / Ireland for
 * the user's specific profile, then surfaces a personalised pick + runner-up.
 *
 * Scoring is intentionally lightweight & transparent — each answer assigns
 * an integer weight (0–3) to each country, summed into a final score.
 * The country with the highest score wins; ties default to the alphabetical
 * order of the option list.
 *
 * UX rule: never block the rest of the comparison page. The recommender
 * sits above the matrix but the user can scroll past it without answering.
 */

const COUNTRIES = [
  { id: 'germany',   name: 'Germany',   flag: '🇩🇪', href: '/study-abroad/germany/student-visa/'   },
  { id: 'australia', name: 'Australia', flag: '🇦🇺', href: '/study-abroad/australia/student-visa/' },
  { id: 'canada',    name: 'Canada',    flag: '🇨🇦', href: '/study-abroad/canada/student-visa/'    },
  { id: 'ireland',   name: 'Ireland',   flag: '🇮🇪', href: '/study-abroad/ireland/student-visa/'   },
];

/* Each answer encodes the score it assigns to {germany, australia, canada, ireland}. */
const QUESTIONS = [
  {
    id: 'budget',
    label: 'What\u2019s your annual all-in budget (tuition + living)?',
    options: [
      { value: 'low',  label: 'Under €15,000',     scores: { germany: 3, australia: 0, canada: 1, ireland: 3 } },
      { value: 'mid',  label: '€15,000 – €25,000', scores: { germany: 2, australia: 1, canada: 3, ireland: 2 } },
      { value: 'high', label: '€25,000+',          scores: { germany: 1, australia: 3, canada: 2, ireland: 1 } },
    ],
  },
  {
    id: 'course',
    label: 'Which best describes your course interest?',
    options: [
      { value: 'stem',       label: 'STEM / Engineering',            scores: { germany: 3, australia: 2, canada: 2, ireland: 1 } },
      { value: 'business',   label: 'Business / Finance',            scores: { germany: 1, australia: 2, canada: 2, ireland: 3 } },
      { value: 'healthcare', label: 'Healthcare / Nursing',          scores: { germany: 1, australia: 3, canada: 3, ireland: 2 } },
      { value: 'humanities', label: 'Humanities / Arts / Languages', scores: { germany: 2, australia: 1, canada: 2, ireland: 3 } },
    ],
  },
  {
    id: 'goal',
    label: 'What\u2019s your post-study intent?',
    options: [
      { value: 'pr',     label: 'PR / Permanent Migration',           scores: { germany: 1, australia: 3, canada: 3, ireland: 1 } },
      { value: 'eu',     label: 'EU work-experience access',          scores: { germany: 3, australia: 0, canada: 0, ireland: 3 } },
      { value: 'work',   label: 'Long post-study work permit (≥ 2y)', scores: { germany: 1, australia: 3, canada: 3, ireland: 2 } },
      { value: 'return', label: 'Return to India with stronger CV',   scores: { germany: 2, australia: 2, canada: 2, ireland: 2 } },
    ],
  },
];

const WINNING_RATIONALE = {
  germany:   'Lowest total cost (often free tuition), strongest STEM ecosystem, and an 18-month job-search residence permit make Germany the practical EU pick — especially under tighter budgets.',
  australia: 'Highest post-study work window (2–4 years), strongest healthcare/STEM pathways, and clear PR migration ladder via Subclass 485 → 189/190. Best if budget allows the higher all-in cost.',
  canada:    'Up-to-3-year PGWP + faster SDS stream + 24 hrs/wk in-term work rights make Canada the most balanced "study-to-PR" route — strongest in healthcare and tech.',
  ireland:   'Lowest published financial threshold (€10,000), EU access, and a 1–2 year Third Level Graduate Programme make Ireland the value pick for business, humanities, and post-study EU career access.',
};

const initialAnswers = { budget: null, course: null, goal: null };

const BestCountryRecommender = () => {
  const [answers, setAnswers] = useState(initialAnswers);
  const [showResult, setShowResult] = useState(false);

  const allAnswered = QUESTIONS.every(q => answers[q.id] !== null);

  /* Score is recomputed only when answers change. Memoised so we don't redo
     the reduce on every render of the inputs. */
  const ranking = useMemo(() => {
    if (!allAnswered) return null;
    const totals = { germany: 0, australia: 0, canada: 0, ireland: 0 };
    QUESTIONS.forEach((q) => {
      const opt = q.options.find(o => o.value === answers[q.id]);
      if (!opt) return;
      Object.keys(totals).forEach((cid) => { totals[cid] += opt.scores[cid]; });
    });
    return Object.entries(totals)
      .map(([id, score]) => ({ id, score, country: COUNTRIES.find(c => c.id === id) }))
      .sort((a, b) => b.score - a.score);
  }, [answers, allAnswered]);

  const handlePick = (qid, value) => {
    setAnswers(a => ({ ...a, [qid]: value }));
    setShowResult(false);
  };

  const handleReveal = () => {
    setShowResult(true);
    // Persist quiz answers + winner to localStorage so the consultation form
    // and other downstream surfaces can auto-fill country/service preferences.
    if (winner) {
      writeVisaRecommendation({
        country: winner.id,
        budget:  answers.budget,
        course:  answers.course,
        goal:    answers.goal,
      });
    }
  };

  const handleReset = () => {
    setAnswers(initialAnswers);
    setShowResult(false);
  };

  const winner = ranking?.[0];
  const runnerUp = ranking?.[1];

  return (
    <section
      className="bg-gradient-to-br from-[#003087] via-[#001F5C] to-slate-950 text-white py-14 lg:py-20"
      data-testid="vc-recommender"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/35 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
            <Sparkles className="w-3.5 h-3.5" /> 60-Second Match
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-syne leading-tight mb-3">
            Best Country for <span className="text-[#FACC15]">My Profile</span>
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Three quick questions. We weight your budget, course interest, and career intent against the 2026 policies of all four countries — then recommend the highest-fit visa.
          </p>
        </div>

        <div className="space-y-6">
          {QUESTIONS.map((q, qIdx) => (
            <fieldset
              key={q.id}
              className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-5 sm:p-6"
              data-testid={`vc-rec-q-${q.id}`}
            >
              <legend className="px-3 py-1 -mt-3 mb-3 bg-[#001F5C] border border-white/10 rounded-full inline-flex items-center gap-2 text-xs">
                <span className="w-5 h-5 rounded-full bg-[#C5A028] text-black text-[11px] font-bold flex items-center justify-center">
                  {qIdx + 1}
                </span>
                <span className="font-semibold tracking-wide">{q.label}</span>
              </legend>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {q.options.map((opt) => {
                  const selected = answers[q.id] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handlePick(q.id, opt.value)}
                      className={`relative text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        selected
                          ? 'border-[#C5A028] bg-[#C5A028]/15 text-white shadow-lg shadow-[#C5A028]/10'
                          : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10 text-slate-200'
                      }`}
                      data-testid={`vc-rec-${q.id}-${opt.value}`}
                      aria-pressed={selected}
                    >
                      <span className="text-sm font-medium leading-tight">{opt.label}</span>
                      {selected && (
                        <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-[#FACC15]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleReveal}
            disabled={!allAnswered}
            className={`inline-flex items-center gap-2 font-bold text-sm sm:text-base px-7 py-3.5 rounded-full transition-all ${
              allAnswered
                ? 'bg-[#C5A028] hover:bg-[#EAB308] text-black hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30'
                : 'bg-white/10 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="vc-rec-reveal"
          >
            <Trophy className="w-4 h-4" />
            {allAnswered ? 'Show My Recommendation' : `Answer ${QUESTIONS.length - Object.values(answers).filter(Boolean).length} more`}
          </button>
          {Object.values(answers).some(Boolean) && (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold text-sm px-5 py-3 rounded-full transition-all"
              data-testid="vc-rec-reset"
            >
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
          )}
        </div>

        {showResult && winner && (
          <div
            className="mt-10 rounded-3xl bg-white text-slate-900 p-6 sm:p-8 shadow-2xl shadow-black/30"
            data-testid="vc-rec-result"
            role="region"
            aria-label="Your country recommendation"
          >
            <div className="flex items-start gap-4 mb-5">
              <div className="text-5xl sm:text-6xl leading-none" aria-hidden="true">{winner.country.flag}</div>
              <div className="flex-1">
                <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#C5A028] mb-1">
                  Your Best-Fit Destination
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087] leading-tight">
                  {winner.country.name}
                </h3>
                <div className="text-xs text-slate-500 mt-1">
                  Match score: <strong className="text-slate-800">{winner.score}</strong>
                  {runnerUp && (
                    <> · runner-up: {runnerUp.country.flag} <strong className="text-slate-800">{runnerUp.country.name}</strong> ({runnerUp.score})</>
                  )}
                </div>
              </div>
            </div>

            <p className="text-sm sm:text-[15px] text-slate-700 leading-relaxed mb-5">
              {WINNING_RATIONALE[winner.id]}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to={`${winner.country.href}?source=visa-comparison-recommender`}
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5"
                data-testid="vc-rec-result-cta"
              >
                Read the {winner.country.name} Visa Guide <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/book-counselling/?source=visa-comparison-recommender"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-6 py-3 rounded-full transition-all"
                data-testid="vc-rec-result-counselling"
              >
                Confirm with a Counsellor
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BestCountryRecommender;
